import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import styles from './ZakatPage.module.css';
import { usePortfolio } from '../hooks/usePortfolio';
import TickerAutocomplete from './TickerAutocomplete';
import ZakatInfoModal from './ZakatInfoModal';

const ZAKAT_RATE = 0.025;
const TICKER_RE  = /^[A-Z0-9.\-]{1,10}$/;

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function fmtMoney(n) {
  if (n == null || isNaN(n)) return '—';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtBig(n) {
  if (!n && n !== 0) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e12) return '$' + (n / 1e12).toFixed(2) + 'T';
  if (abs >= 1e9)  return '$' + (n / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6)  return '$' + (n / 1e6).toFixed(2) + 'M';
  return '$' + n.toLocaleString();
}

function calcZakatable(method, price, shares, criPerShare, precisePct) {
  const mv = price * shares;
  if (method === 'simple')  return mv;
  if (method === '25pct')   return mv * 0.25;
  if (method === 'precise') return mv * (precisePct / 100);
  if (method === 'cri')     return criPerShare * shares;
  return 0;
}

export default function ZakatPage({ theme, onBack }) {
  const { holdings, addHolding, updateHolding, removeHolding, clearHoldings } = usePortfolio();

  const [zakatDate,    setZakatDate]    = useState(todayStr());
  const [investorType, setInvestorType] = useState('long-term');
  const [method,       setMethod]       = useState('25pct');
  const [precisePct,   setPrecisePct]   = useState(25);
  const [infoTopic,    setInfoTopic]    = useState(null); // 'zakatDate' | 'investorType' | 'methods'

  // Per-ticker fetched data: { [ticker]: { price, actualDate, isApproximation, loading, error } }
  const [priceMap, setPriceMap] = useState({});
  // Per-ticker CRI data: { [ticker]: { cash, receivables, inventory, sharesOut, criPerShare } }
  const [criMap,   setCriMap]   = useState({});

  // Add-row state
  const [newTicker, setNewTicker] = useState('');
  const [newShares, setNewShares] = useState('');
  const [addError,  setAddError]  = useState('');
  const tickerInputRef = useRef(null);

  // ── Fetch price for a single ticker ──────────────────────────────────────
  const fetchPriceFor = useCallback(async (ticker) => {
    if (!zakatDate) return;
    setPriceMap(prev => ({ ...prev, [ticker]: { ...(prev[ticker] || {}), loading: true, error: '' } }));
    try {
      const { data } = await axios.get(`/api/price-on-date/${ticker}?date=${zakatDate}`);
      setPriceMap(prev => ({ ...prev, [ticker]: { price: data.price, actualDate: data.actualDate, isApproximation: data.isApproximation, loading: false, error: '' } }));
    } catch (err) {
      const msg = err.response?.data?.error || 'Price unavailable';
      setPriceMap(prev => ({ ...prev, [ticker]: { loading: false, error: msg } }));
    }
  }, [zakatDate]);

  // ── Fetch CRI data for a single ticker via /api/screen ───────────────────
  const fetchCriFor = useCallback(async (ticker) => {
    setCriMap(prev => ({ ...prev, [ticker]: { ...(prev[ticker] || {}), loading: true, error: '' } }));
    try {
      const { data } = await axios.post('/api/screen', { ticker, depth: 1 });
      const f = data?.financials || {};
      const cash        = (f.cashAndEquivalents || 0) + (f.shortTermInvestments || 0);
      const receivables = f.netReceivables    || 0;
      const inventory   = f.inventory         || 0;
      const sharesOut   = f.sharesOutstanding || 0;
      const criPerShare = sharesOut > 0 ? (cash + receivables + inventory) / sharesOut : 0;
      setCriMap(prev => ({ ...prev, [ticker]: { cash, receivables, inventory, sharesOut, criPerShare, loading: false, error: '' } }));
    } catch {
      setCriMap(prev => ({ ...prev, [ticker]: { loading: false, error: 'Data unavailable' } }));
    }
  }, []);

  // ── Re-fetch all prices when date changes ────────────────────────────────
  useEffect(() => {
    if (!holdings.length) return;
    holdings.forEach(h => fetchPriceFor(h.ticker));
  }, [zakatDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch CRI data when switching to CRI method ──────────────────────────
  useEffect(() => {
    if (method !== 'cri' || !holdings.length) return;
    holdings.forEach(h => {
      if (!criMap[h.ticker]) fetchCriFor(h.ticker);
    });
  }, [method]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Add a holding ────────────────────────────────────────────────────────
  function handleAdd() {
    const t = newTicker.toUpperCase().trim();
    const s = parseFloat(newShares);
    if (!TICKER_RE.test(t))  { setAddError('Invalid ticker symbol.'); return; }
    if (!s || s <= 0)        { setAddError('Enter a valid share count.'); return; }
    if (holdings.find(h => h.ticker === t)) { setAddError(`${t} already in portfolio.`); return; }
    setAddError('');
    addHolding(t, s);
    fetchPriceFor(t);
    if (method === 'cri') fetchCriFor(t);
    setNewTicker('');
    setNewShares('');
    tickerInputRef.current?.focus();
  }

  function handleAddKey(e) {
    if (e.key === 'Enter') handleAdd();
  }

  // ── Calculations ─────────────────────────────────────────────────────────
  const effectiveMethod = investorType === 'day-trader' ? 'simple' : method;

  const rows = holdings.map(h => {
    const pd  = priceMap[h.ticker] || {};
    const cd  = criMap[h.ticker]   || {};
    const price = pd.price || 0;
    const criPerShare = cd.criPerShare || 0;
    const zakatable = price > 0 || (effectiveMethod === 'cri' && criPerShare > 0)
      ? calcZakatable(effectiveMethod, price, h.shares, criPerShare, precisePct)
      : null;
    return { ...h, pd, cd, price, zakatable, zakat: zakatable != null ? zakatable * ZAKAT_RATE : null };
  });

  const totalZakatable = rows.reduce((s, r) => r.zakatable != null ? s + r.zakatable : s, 0);
  const totalZakat     = totalZakatable * ZAKAT_RATE;
  const hasAnyResult   = rows.some(r => r.zakatable != null);

  const methodOptions = [
    { key: '25pct',   label: '25% Rule',       rec: true  },
    { key: 'simple',  label: 'Simple (2.5%)',   rec: false },
    { key: 'precise', label: 'Precise %',       rec: false },
    { key: 'cri',     label: 'CRI Method',      rec: false },
  ];

  return (
    <div className={styles.page}>

      {/* ── Page header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <button className={styles.backBtn} onClick={onBack}>← Back</button>
          <h2 className={styles.pageTitle}>Zakat Portfolio Calculator</h2>
          <p className={styles.pageSub}>
            Calculate Zakat due across your stock holdings. Portfolio saved in this browser session.
          </p>
        </div>
        {holdings.length > 0 && (
          <button className={styles.clearBtn} onClick={clearHoldings}>Clear all</button>
        )}
      </div>

      {/* ── Global settings ── */}
      <div className={styles.settingsRow}>
        <div className={styles.settingField}>
          <label className={styles.settingLabel}>
            Zakat year-end date
            <button className={styles.infoBtn} onClick={() => setInfoTopic('zakatDate')} aria-label="More info">ⓘ</button>
          </label>
          <input
            className={styles.settingInput}
            type="date"
            max={todayStr()}
            value={zakatDate}
            onChange={e => setZakatDate(e.target.value)}
          />
        </div>

        <div className={styles.settingField}>
          <label className={styles.settingLabel}>
            Investor type
            <button className={styles.infoBtn} onClick={() => setInfoTopic('investorType')} aria-label="More info">ⓘ</button>
          </label>
          <div className={styles.toggleRow}>
            {[
              { key: 'day-trader', label: 'Day Trader' },
              { key: 'long-term',  label: 'Long-Term'  },
            ].map(({ key, label }) => (
              <button
                key={key}
                className={`${styles.toggleBtn} ${investorType === key ? styles.toggleBtnActive : ''}`}
                onClick={() => setInvestorType(key)}
              >{label}</button>
            ))}
          </div>
        </div>

        {investorType === 'long-term' && (
          <div className={styles.settingField}>
            <label className={styles.settingLabel}>
            Method
            <button className={styles.infoBtn} onClick={() => setInfoTopic('methods')} aria-label="More info">ⓘ</button>
          </label>
            <div className={styles.toggleRow}>
              {methodOptions.map(({ key, label, rec }) => (
                <button
                  key={key}
                  className={`${styles.toggleBtn} ${method === key ? styles.toggleBtnActive : ''}`}
                  onClick={() => setMethod(key)}
                  title={rec ? 'Recommended' : undefined}
                >{label}{rec ? ' ★' : ''}</button>
              ))}
            </div>
          </div>
        )}

        {investorType === 'long-term' && method === 'precise' && (
          <div className={styles.settingField}>
            <label className={styles.settingLabel}>Zakatable %</label>
            <div className={styles.preciseRow}>
              <input
                className={styles.preciseInput}
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={precisePct}
                onChange={e => setPrecisePct(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
              />
              <span className={styles.preciseUnit}>%</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Holdings table ── */}
      {holdings.length > 0 ? (
        <>
          <div className={styles.tableWrap}>
            {/* Header */}
            <div className={styles.tableHeader}>
              <span className={styles.colTicker}>Ticker</span>
              <span className={styles.colShares}>Shares</span>
              <span className={styles.colPrice}>
                {effectiveMethod === 'cri' ? 'CRI / Share' : 'Price on Date'}
              </span>
              <span className={styles.colZakatable}>Zakatable</span>
              <span className={styles.colZakat}>Zakat Owed</span>
              <span className={styles.colRemove} />
            </div>

            {/* Rows */}
            {rows.map(row => {
              const isLoading = row.pd.loading || (effectiveMethod === 'cri' && row.cd.loading);
              const hasError  = row.pd.error   || (effectiveMethod === 'cri' && row.cd.error);
              const displayPrice = effectiveMethod === 'cri'
                ? (row.cd.criPerShare > 0 ? fmtMoney(row.cd.criPerShare) : '—')
                : (row.price > 0 ? fmtMoney(row.price) : '—');

              return (
                <div key={row.id} className={styles.tableRow}>
                  <span className={styles.colTicker}>
                    <span className={styles.tickerLabel}>{row.ticker}</span>
                    {row.pd.isApproximation && (
                      <span className={styles.approxNote} title={`Using price from ${row.pd.actualDate}`}>~</span>
                    )}
                  </span>
                  <span className={styles.colShares}>
                    <input
                      className={styles.sharesInput}
                      type="number"
                      min="0"
                      step="any"
                      value={row.shares}
                      onChange={e => updateHolding(row.id, e.target.value)}
                    />
                  </span>
                  <span className={styles.colPrice}>
                    {isLoading ? <span className={styles.loadingDot}>…</span> : (hasError ? <span className={styles.errTxt}>!</span> : displayPrice)}
                  </span>
                  <span className={styles.colZakatable}>
                    {isLoading ? '…' : (row.zakatable != null ? fmtMoney(row.zakatable) : '—')}
                  </span>
                  <span className={`${styles.colZakat} ${styles.zakatVal}`}>
                    {isLoading ? '…' : (row.zakat != null ? fmtMoney(row.zakat) : '—')}
                  </span>
                  <span className={styles.colRemove}>
                    <button className={styles.removeBtn} onClick={() => removeHolding(row.id)} aria-label="Remove">✕</button>
                  </span>
                </div>
              );
            })}
          </div>

          {/* Totals */}
          {hasAnyResult && (
            <div className={styles.totalsRow}>
              <div className={styles.totalItem}>
                <span className={styles.totalLabel}>Total Zakatable</span>
                <span className={styles.totalValue}>{fmtMoney(totalZakatable)}</span>
              </div>
              <div className={`${styles.totalItem} ${styles.totalItemHighlight}`}>
                <span className={styles.totalLabel}>Total Zakat Owed (2.5%)</span>
                <span className={styles.totalZakat}>{fmtMoney(totalZakat)}</span>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>No holdings yet</p>
          <p className={styles.emptySub}>Add stocks below to calculate your Zakat obligation.</p>
        </div>
      )}

      {/* ── Add row ── */}
      <div className={styles.addRow}>
        <TickerAutocomplete
          value={newTicker}
          onChange={v => { setNewTicker(v); setAddError(''); }}
          onSelect={v => { setNewTicker(v); setAddError(''); }}
          placeholder="Ticker or company name"
          inputRef={tickerInputRef}
        />
        <input
          className={styles.addInput}
          type="number"
          placeholder="Shares"
          min="0"
          step="any"
          value={newShares}
          onChange={e => { setNewShares(e.target.value); setAddError(''); }}
          onKeyDown={handleAddKey}
        />
        <button className={styles.addBtn} onClick={handleAdd}>+ Add Stock</button>
      </div>
      {addError && <p className={styles.addError}>{addError}</p>}

      {infoTopic && <ZakatInfoModal topic={infoTopic} onClose={() => setInfoTopic(null)} />}

      <p className={styles.disclaimer}>
        All methods are based on recognized scholarly opinions. Balance sheet data from the most recent
        annual SEC filing. This is an estimate — consult a qualified Islamic scholar for a binding ruling.
        Portfolio is stored in this browser session only and is never sent to our servers.
      </p>
    </div>
  );
}
